import { useCallback, useState } from 'react'
import { Button, Flex, Form, Skeleton, notification } from 'antd'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { menuUrls } from 'src/app/shared/constants/menu.constants'
import { formatDataBeforeSubmit, generateValidationSchema, mappingData } from '../components/utils'
import '../components/index.scss'
import FormPayee from '../components/formPayee'
import { editPayee, getPayeeMatchParametersDetail } from 'src/app/shared/services/PayeeMatchParameters'
import { IPayloadPayeeMatchParameters } from 'src/app/shared/models/payeeMatchParameters.model'
import { SYSTEM_MESSAGE_IDS } from 'src/app/shared/constants/systemMessages.constants'
import { yupResolver } from '@hookform/resolvers/yup'

const EditPayeeMatchParameters = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const params = useParams()
  const { id } = params
  const [isFetched, setIsFetched] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [api, contextHolder] = notification.useNotification()

  const {
    control,
    formState: { errors, isValid, dirtyFields },
    handleSubmit,
    setError,
    setValue,
  } = useForm<any>({
    mode: 'all',
    defaultValues: async () => await getPayee(),
    resolver: yupResolver(generateValidationSchema(t)),
  })
  const handleCancel = () => {
    navigate(menuUrls.payeeMatchParameters)
  }
  const profileName = useWatch({
    control,
    name: 'profileName',
  })

  const getPayee = useCallback(async () => {
    if (id) {
      const res = await getPayeeMatchParametersDetail(id)

      const formattedData = mappingData(res?.data)
      setIsFetched(true)
      return formattedData
    }
    navigate(menuUrls.bankUserSetup)
  }, [id, navigate])

  const editPayeeMatchParameters = async (data: IPayloadPayeeMatchParameters) => {
    setIsLoading(true)

    try {
      const res = await editPayee(formatDataBeforeSubmit(data), id)
      const { statusCode } = res

      // No Duplicate
      if (statusCode === 'Success') {
        setIsLoading(false)
        navigate(menuUrls.payeeMatchParameters, {
          state: {
            payeeMatchParameters: {
              type: 'success',
              message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.UPDATE_SUCCESSFULLY, {
                Object: data?.profileName,
              }),
            },
          },
        })
      }
    } catch (error: any) {
      if (error?.statusCode === 'PPA06') {
        setError('profileName', {
          message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.OBJECT_DUPLICATED, {
            Object: data?.profileName,
          }),
        })
        setIsLoading(false)
      }
    }
  }
  const isSaveButtonDisabled =
    !!Object.keys(errors)?.length || !isValid || !profileName || !Object.keys(dirtyFields)?.length

  const [form] = Form.useForm()

  return (
    <>
      {contextHolder}
      <Form
        form={form}
        name='payee-match-parameter-form'
        className='payee-match-parameter-form-container'
        layout='vertical'
        onFinish={handleSubmit(editPayeeMatchParameters)}
      >
        {isFetched ? (
          <FormPayee control={control} errors={errors} isViewOnly={false} setValue={setValue} />
        ) : (
          <Skeleton />
        )}

        <Flex className='button-group' justify='flex-end' gap={24}>
          <Button className='cancel-button' onClick={handleCancel}>
            {t('OverallFunctionality:cancel_btn')}
          </Button>
          <Button
            className='save-button'
            type='primary'
            disabled={isSaveButtonDisabled}
            htmlType='button'
            onClick={form.submit}
            loading={isLoading}
          >
            {t('OverallFunctionality:save_changes_btn')}
          </Button>
        </Flex>
      </Form>
    </>
  )
}

export default EditPayeeMatchParameters
